import React, { useContext, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthContext } from './context/AuthContext';
import { useI18n } from './i18n';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import Main from './pages/Main';
import Profile from './pages/Profile';
import ProfileGallery from './pages/ProfileGallery';
import Conciencia from './pages/Conciencia';
import Subconciencia from './pages/Subconciencia';
import Ensonacion from './pages/Ensonacion';
import ConcienciaAbout from './pages/ConcienciaAbout';
import Questionnaire from './pages/Questionnaire';
import Tareas from './pages/Tareas';
import TareasVisuales from './pages/TareasVisuales';
import Opciones from './pages/Opciones';
import Ajustes from './pages/Ajustes';
import AvatarSelection from './pages/AvatarSelection';
import Acerca from './pages/Acerca';
import Contact from './pages/Contact';
import AgentQGuide from './pages/AgentQGuide';
import NetworkPage from './pages/NetworkPage';
import QophPage from './pages/QophPage';
import Hiperstision from './pages/Hiperstision';
import AutoConexion from './pages/AutoConexion';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import { AnimatePresence } from 'framer-motion';
import HeaderBar from './components/HeaderBar';
import AgentWidget from './components/AgentWidget';
import AgentOrchestrator from './components/AgentOrchestrator';
import GlobalMusicPlayer from './components/GlobalMusicPlayer';
import GlobalScrollControls from './components/GlobalScrollControls';
import FloatingTtsControl from './components/FloatingTtsControl';
import GlobalCyberBackground from './components/GlobalCyberBackground';
import GlobalSynapticNodes from './components/GlobalSynapticNodes';
import SleepScreen from './components/SleepScreen';
import GuardianOfZone from './components/guardians/GuardianOfZone';
import MagicLinkHandler from './components/MagicLinkHandler';
import useCircadianState from './hooks/useCircadianState';
import useGlobalSpeechAction from './hooks/useGlobalSpeechAction';
import './styles/App.css';
import { AppBackgroundProvider } from './context/AppBackgroundContext';
import { NotificationProvider } from './context/NotificationContext';
import { ProgressionProvider, useProgression } from './context/ProgressionContext';
import FloatingNotification from './components/FloatingNotification';
import { AgentCommandProvider } from './contexts/AgentCommandContext';
import Inquisition from './pages/Inquisition';

function getCyberpunkPreference() {
  const stored = localStorage.getItem('cyberpunkMode');
  return stored === null ? true : stored === 'true';
}

function getSolarpunkPreference() {
  const stored = localStorage.getItem('solarpunkMode');
  return stored === 'true';
}

function applyThemePreferences() {
  try {
    const isSolarpunk = getSolarpunkPreference();
    const isCyberpunk = getCyberpunkPreference();

    if (isSolarpunk) {
      document.documentElement.setAttribute('data-solarpunk', 'true');
      document.documentElement.setAttribute('data-cyberpunk', 'false');
    } else {
      document.documentElement.setAttribute('data-solarpunk', 'false');
      document.documentElement.setAttribute('data-cyberpunk', isCyberpunk ? 'true' : 'false');
    }
  } catch (e) {
    try { document.documentElement.setAttribute('data-cyberpunk', 'true'); } catch (_) {}
  }
}

function AppContentInner() {
  const { logout, user, updateUserAvatar, questionnaireState, handleQuestionnaireComplete, handleSkip } = useContext(AuthContext);
  const { t } = useI18n();
  const { currentState } = useCircadianState();
  const { progressionState, loading: progressionLoading } = useProgression();
  useGlobalSpeechAction();

  if (!user) {
    return <div>{t('common.loadingUser')}</div>;
  }

  if (progressionLoading && questionnaireState !== 'pending') {
    return <div>Cargando estado de la IA...</div>;
  }

  if (questionnaireState === 'pending') {
    return (
      <Routes>
        <Route
          path="/questionnaire"
          element={<Questionnaire onComplete={handleQuestionnaireComplete} onSkip={handleSkip} />}
        />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="*" element={<Navigate to="/questionnaire" />} />
      </Routes>
    );
  }

  const isTutorialActive = progressionState && progressionState.stage !== 'COMPLETED';

  return (
    <AppBackgroundProvider>
      <NotificationProvider>
        <AgentCommandProvider>
        <div className="app-container">
          <FloatingNotification />
          <AnimatePresence>
            {currentState === 'SLEEPING' && <SleepScreen />}
          </AnimatePresence>
          <GlobalSynapticNodes />
          <GlobalMusicPlayer />
          <AgentOrchestrator />
          {isTutorialActive ? null : <GuardianOfZone />}
          <HeaderBar
            logout={logout}
            user={user}
            updateUserAvatar={updateUserAvatar}
            questionnaireState={questionnaireState}
          />
          <div className="page-content">
            {isTutorialActive ? (
              <Routes>
                <Route path="/" element={<Main />} />
                {progressionState?.stage === 'ORACULO' && <Route path="/qoph" element={<QophPage />} />}
                {progressionState?.stage === 'ENSONACION' && <Route path="/ensonacion" element={<Ensonacion />} />}
                {progressionState?.stage === 'TAREAS' && <Route path="/tareas/:id?" element={<Tareas />} />}
                {progressionState?.stage === 'CONCIENCIA' && <Route path="/conciencia" element={<Conciencia />} />}
                {progressionState?.stage === 'SUBCONCIENCIA' && <Route path="/subconciencia" element={<Subconciencia />} />}
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            ) : (
              <Routes>
                <Route path="/" element={<Main />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/profile/gallery" element={<ProfileGallery />} />
                <Route path="/network" element={<NetworkPage />} />
                <Route path="/qoph" element={<QophPage />} />
                <Route path="/hiperstision" element={<Hiperstision />} />
                <Route path="/inquisition" element={<Inquisition />} />
                <Route path="/conciencia" element={<Conciencia />} />
                <Route path="/subconciencia" element={<Subconciencia />} />
                <Route path="/ensonacion" element={<Ensonacion />} />
                <Route path="/conciencia/about" element={<ConcienciaAbout />} />
                <Route path="/tareas/:id?" element={<Tareas />} />
                <Route path="/lugares" element={<TareasVisuales />} />
                <Route path="/opciones" element={<Opciones />} />
                <Route path="/ajustes" element={<Ajustes />} />
                <Route path="/avatar" element={<AvatarSelection />} />
                <Route path="/acerca" element={<Acerca />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/acerca/agenteq" element={<AgentQGuide />} />
                <Route path="/auto-conexion" element={<AutoConexion />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route
                  path="/questionnaire"
                  element={<Questionnaire onComplete={handleQuestionnaireComplete} onSkip={handleSkip} />}
                />
                <Route path="/demo" element={<MagicLinkHandler />} />
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            )}
          </div>
          <div className="floating-controls-right">
            <FloatingTtsControl />
            <AgentWidget />
          </div>
          <GlobalScrollControls />
        </div>
        </AgentCommandProvider>
      </NotificationProvider>
    </AppBackgroundProvider>
  );
}

function AppContent() {
  return (
    <ProgressionProvider>
      <AppContentInner />
    </ProgressionProvider>
  );
}

function App() {
  const { token, login } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    applyThemePreferences();
  }, []);

  const handleAuthSuccess = (newToken) => {
    login(newToken);
    navigate('/');
  };

  if (!token) {
    return (
      <>
        <GlobalCyberBackground />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login onLoginSuccess={handleAuthSuccess} />} />
          <Route path="/register" element={<Register onRegisterSuccess={handleAuthSuccess} />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/demo" element={<MagicLinkHandler />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </>
    );
  }

  return (
    <>
      <GlobalCyberBackground />
      <AppContent />
    </>
  );
}

export default App;
